import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef } from "react";
import { createPortal } from "react-dom";
import { useTrademarkCompliance } from "./useTrademarkCompliance";
const STYLES = {
    badge: {
        position: "absolute",
        bottom: "40pt",
        left: "10px",
        backdropFilter: "none",
        WebkitBackdropFilter: "none",
        background: "transparent",
        border: "none",
        borderRadius: 0,
        padding: 0,
        fontSize: "11px",
        fontWeight: 500,
        color: "rgba(255, 255, 255, 0.35)",
        cursor: "pointer",
        transition: "color 0.2s ease",
        userSelect: "none",
        whiteSpace: "nowrap",
        display: "flex",
        alignItems: "center",
        gap: "4px",
        animation: "vnscTmSlideIn 0.3s ease-out",
        zIndex: 100,
    },
    icon: {
        fontSize: "11px",
        color: "rgba(99, 102, 241, 0.5)",
        transition: "color 0.2s ease",
    },
};
const DEGRADE_ROOT_ID = "vninvestcharts-widget-tm-degrade";
function getDegradeRoot() {
    if (typeof document === "undefined")
        return null;
    ensureKeyframes();
    let root = document.getElementById(DEGRADE_ROOT_ID);
    if (!root) {
        root = document.createElement("div");
        root.id = DEGRADE_ROOT_ID;
        root.style.position = "fixed";
        root.style.top = "0";
        root.style.left = "0";
        root.style.width = "100%";
        root.style.height = "100%";
        root.style.zIndex = "99999";
        root.style.pointerEvents = "none";
        document.body.appendChild(root);
    }
    return root;
}
function ensureKeyframes() {
    if (typeof document === "undefined")
        return;
    const id = "vninvestcharts-widget-tm-keyframes";
    if (document.getElementById(id))
        return;
    const style = document.createElement("style");
    style.id = id;
    style.textContent = `
@keyframes vnscTmPulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.1); } }
@keyframes vnscTmSlideIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
`;
    document.head.appendChild(style);
}
const DEGRADE_STYLES = {
    degradeOverlay: {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.95)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 99999,
        color: "#ffffff",
        fontFamily: "var(--gc-font, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif)",
        padding: "24px",
        boxSizing: "border-box",
        pointerEvents: "auto",
    },
    degradeContent: {
        textAlign: "center",
        maxWidth: "400px",
        padding: "24px",
    },
    degradeIcon: {
        fontSize: "48px",
        marginBottom: "16px",
        animation: "vnscTmPulse 2s infinite ease-in-out",
    },
    degradeTitle: {
        margin: "0 0 12px 0",
        fontSize: "20px",
        fontWeight: 600,
    },
    degradeDescription: {
        margin: "0 0 24px 0",
        fontSize: "14px",
        lineHeight: "1.6",
        color: "#d1d5db",
    },
    degradeBtn: {
        backgroundColor: "#2962ff",
        color: "#ffffff",
        border: "none",
        padding: "12px 24px",
        borderRadius: "6px",
        fontSize: "14px",
        fontWeight: 600,
        cursor: "pointer",
        transition: "background-color 0.2s ease",
    },
};
const TRADEMARK_DEFAULT_TARGET_URL = "https://vninvest.edusuccess.vn";
export function EngineTrademark({ patTier, enforcementEnabled = false, containerRef, targetUrl = TRADEMARK_DEFAULT_TARGET_URL }) {
    const badgeRef = useRef(null);
    const compliance = useTrademarkCompliance({
        patTier,
        enforcementEnabled,
        badgeRef,
    });
    if (!compliance.shouldShow)
        return null;
    if (compliance.isDegraded) {
        const portalRoot = getDegradeRoot();
        if (!portalRoot)
            return null;
        return createPortal(_jsx("div", { style: DEGRADE_STYLES.degradeOverlay, "data-testid": "trademark-degraded", children: _jsxs("div", { style: DEGRADE_STYLES.degradeContent, children: [_jsx("div", { style: DEGRADE_STYLES.degradeIcon, children: "\u26A0\uFE0F" }), _jsx("h2", { style: DEGRADE_STYLES.degradeTitle, children: "Ph\u00E1t hi\u1EC7n can thi\u1EC7p nh\u00E3n hi\u1EC7u" }), _jsx("p", { style: DEGRADE_STYLES.degradeDescription, children: "Widget y\u00EAu c\u1EA7u hi\u1EC3n th\u1ECB nh\u00E3n \"Powered by VNInvest\" \u0111\u1EC3 ho\u1EA1t \u0111\u1ED9ng. Vui l\u00F2ng kh\u00F4i ph\u1EE5c hi\u1EC3n th\u1ECB nh\u00E3n \u0111\u1EC3 ti\u1EBFp t\u1EE5c s\u1EED d\u1EE5ng." }), _jsx("button", { onClick: compliance.onRestore, style: DEGRADE_STYLES.degradeBtn, children: "Kh\u00F4i ph\u1EE5c v\u00E0 ti\u1EBFp t\u1EE5c" })] }) }), portalRoot);
    }
    const badge = (_jsxs("div", { ref: badgeRef, style: STYLES.badge, "data-testid": "trademark-badge", role: "link", tabIndex: 0, onClick: () => window.open(targetUrl, "_blank", "noopener,noreferrer"), onKeyDown: (e) => {
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                window.open(targetUrl, "_blank", "noopener,noreferrer");
            }
        }, "aria-label": "Powered by VNInvest - Click to visit website", children: [_jsx("span", { style: STYLES.icon, children: "\u26A1" }), "vninvest"] }));
    if (containerRef?.current) {
        return createPortal(badge, containerRef.current);
    }
    return badge;
}
